import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "../api";
import {
  Users, MessageSquare, Star, ChevronDown, X, Send, RefreshCw,
  CheckCircle2, XCircle, Clock, Phone, Award, Mail, GraduationCap,
  Calendar, Video, MapPin, Sparkles, Briefcase, FileText,
} from "lucide-react";
import DownloadResumeButton from "../components/hr/DownloadResumeButton";
import InterviewSchedulerModal from "../components/hr/InterviewSchedulerModal";
import InterviewFeedbackModal from "../components/hr/InterviewFeedbackModal";
import ResumeInsights from "../components/hr/ResumeInsights";
import ResumePreview from "../components/hr/ResumePreview";

/* ------------------------- stage metadata ------------------------- */
const STAGES = [
  { key: "pending",    label: "New",         color: "#0099FF", desc: "Just applied" },
  { key: "screening",  label: "Screening",   color: "#0040A8", desc: "Reviewing profile" },
  { key: "interview",  label: "Interview",   color: "#9933FF", desc: "In interview loop" },
  { key: "offer",      label: "Offer",       color: "#16A34A", desc: "Offer extended" },
  { key: "hired",      label: "Hired",       color: "#15803D", desc: "Joined the team" },
  { key: "rejected",   label: "Rejected",    color: "#DC2626", desc: "Not moving forward" },
  { key: "withdrawn",  label: "Withdrawn",   color: "#94A3B8", desc: "Candidate withdrew" },
];
const NEXT_STAGE = {
  pending: "screening",
  screening: "interview",
  interview: "offer",
  offer: "hired",
};

/* ------------------------- helpers ------------------------- */
function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function initials(name = "") {
  return name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}

/* ------------------------- card ------------------------- */
function ApplicantCard({ app, onOpen, onAdvance, onReject }) {
  const score = app.scorecard_avg;
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen(app);
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(app)}
      onKeyDown={handleKey}
      data-testid={`pipeline-card-${app.id}`}
      className="w-full text-left card card-pad space-y-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md group cursor-pointer focus:outline-none focus:ring-2 focus:ring-signal/40"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-full grid place-items-center text-white text-xs font-bold shrink-0"
          style={{ background: "linear-gradient(135deg, #006CFF 0%, #9933FF 100%)" }}
        >
          {initials(app.student_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-ink truncate">{app.student_name || "Unnamed"}</div>
          <div className="text-xs text-muted truncate">{app.headline || app.student_email}</div>
        </div>
      </div>
      {app.skills?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {app.skills.slice(0, 3).map((s) => (
            <span key={s} className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded bg-paper border" style={{ borderColor: "var(--rule)" }}>{s}</span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-muted pt-1.5 border-t" style={{ borderColor: "var(--rule)" }}>
        <span className="inline-flex items-center gap-1"><Clock size={12} />{timeAgo(app.created_at)}</span>
        <span className="inline-flex items-center gap-2">
          {app.has_resume && (
            <span title="Resume attached" className="inline-flex items-center gap-0.5 text-emerald-700" data-testid={`card-has-resume-${app.id}`}>
              <CheckCircle2 size={12} /> CV
            </span>
          )}
          {app.notes_count > 0 && (
            <span className="inline-flex items-center gap-0.5"><MessageSquare size={12} />{app.notes_count}</span>
          )}
          {score !== null && score !== undefined && (
            <span className="inline-flex items-center gap-0.5 font-semibold" style={{ color: "#9933FF" }}>
              <Star size={12} className="fill-current" />{score}
            </span>
          )}
        </span>
      </div>
      {NEXT_STAGE[app.status] && (
        <div className="flex items-center gap-2 pt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAdvance(app); }}
            className="flex-1 text-[11px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-md bg-signal text-white hover:bg-signalDark transition"
            data-testid={`advance-${app.id}`}
          >
            → {STAGES.find((s) => s.key === NEXT_STAGE[app.status])?.label}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onReject(app); }}
            className="text-[11px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition"
            data-testid={`reject-${app.id}`}
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------- column ------------------------- */
function Column({ stage, items, onOpen, onAdvance, onReject }) {
  return (
    <div className="min-w-[280px] max-w-[280px] flex flex-col gap-3" data-testid={`column-${stage.key}`}>
      <div className="flex items-center justify-between sticky top-0 z-10 bg-paper py-2">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: stage.color }} />
          <span className="font-display font-bold text-sm uppercase tracking-wider" style={{ color: stage.color }}>
            {stage.label}
          </span>
          <span className="text-xs text-muted font-mono">{items.length}</span>
        </div>
      </div>
      <div className="space-y-3 flex-1">
        {items.length === 0 ? (
          <div className="border-2 border-dashed rounded-xl p-6 text-center text-xs text-muted" style={{ borderColor: "var(--rule)" }}>
            {stage.desc}
          </div>
        ) : items.map((a) => (
          <ApplicantCard key={a.id} app={a} onOpen={onOpen} onAdvance={onAdvance} onReject={onReject} />
        ))}
      </div>
    </div>
  );
}

/* ------------------------- detail drawer ------------------------- */
function DetailDrawer({ app, onClose, onChanged }) {
  const [tab, setTab] = useState("interviews");
  const [notes, setNotes] = useState([]);
  const [scorecard, setScorecard] = useState({ items: [], average: null, count: 0 });
  const [interviews, setInterviews] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showScheduler, setShowScheduler] = useState(false);
  const [feedbackFor, setFeedbackFor] = useState(null);

  const refresh = useCallback(async () => {
    setErr("");
    try {
      const [n, s, iv] = await Promise.all([
        api.get(`/applications/${app.id}/notes`),
        api.get(`/applications/${app.id}/scorecards`),
        api.get(`/applications/${app.id}/interviews`).catch(() => ({ data: [] })),
      ]);
      setNotes(n.data || []);
      setScorecard(s.data || { items: [], average: null, count: 0 });
      setInterviews(iv.data || []);
    } catch (e) {
      setErr(formatError(e));
    }
  }, [app.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/applications/${app.id}/notes`, { text: noteText, visibility: "team" });
      setNoteText("");
      await refresh();
      onChanged?.();
    } catch (e2) { setErr(formatError(e2)); }
    finally { setBusy(false); }
  };

  const submitScore = async (e) => {
    e.preventDefault();
    if (!rating || busy) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/applications/${app.id}/scorecard`, { rating, criterion: "overall", comment });
      setRating(0); setComment("");
      await refresh();
      onChanged?.();
    } catch (e2) { setErr(formatError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="applicant-drawer">
      <button type="button" aria-label="Close" onClick={onClose} className="flex-1 bg-black/40 backdrop-blur-sm" />
      <aside className="w-full max-w-md bg-white shadow-2xl flex flex-col" style={{ animation: "slideInFromRight 240ms ease-out" }}>
        <header className="p-5 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--rule)" }}>
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full grid place-items-center text-white text-sm font-bold"
                style={{ background: "linear-gradient(135deg, #006CFF 0%, #9933FF 100%)" }}>
                {initials(app.student_name)}
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-base truncate">{app.student_name}</div>
                <div className="text-xs text-muted truncate inline-flex items-center gap-1"><Mail size={11} />{app.student_email}</div>
              </div>
            </div>
            {/* Contact & sourcing details — surfaced from CSV/single-add ingestion */}
            {(app.student_phone || app.current_role || app.current_company || (app.is_sourced && app.source)) && (
              <div className="mt-3 grid grid-cols-1 gap-1.5 text-xs" data-testid="applicant-contact-block">
                {app.student_phone && (
                  <div className="inline-flex items-center gap-1.5 text-slate-700" data-testid="applicant-phone">
                    <Phone size={11} className="text-muted shrink-0" />
                    <a href={`tel:${app.student_phone}`} className="hover:text-signal transition">{app.student_phone}</a>
                  </div>
                )}
                {(app.current_role || app.current_company) && (
                  <div className="inline-flex items-center gap-1.5 text-slate-700" data-testid="applicant-current-role">
                    <Briefcase size={11} className="text-muted shrink-0" />
                    <span>
                      {app.current_role || "—"}
                      {app.current_company && (<> at <b className="text-slate-900">{app.current_company}</b></>)}
                    </span>
                  </div>
                )}
                {app.is_sourced && app.source && (
                  <div className="inline-flex items-center gap-1.5 text-slate-700" data-testid="applicant-source">
                    <MessageSquare size={11} className="text-muted shrink-0" />
                    <span>Sourced via <b className="text-purple-700">{app.source}</b></span>
                  </div>
                )}
              </div>
            )}
            {app.skills?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {app.skills.slice(0, 8).map((s) => (
                  <span key={s} className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded bg-paper border" style={{ borderColor: "var(--rule)" }}>{s}</span>
                ))}
              </div>
            )}
            {app.coe_badges?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {app.coe_badges.map((b) => (
                  <span key={b.lab_code || b.lab_id} className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded text-white inline-flex items-center gap-1"
                    style={{ background: b.lab_color || "#9933FF" }}>
                    <Award size={10} />{b.lab_code}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-md hover:bg-paper grid place-items-center" data-testid="drawer-close">
            <X size={18} />
          </button>
        </header>

        {/* Resume + Send Offer */}
        <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--rule)" }}>
          <DownloadResumeButton
            appId={app.id}
            filename={app.resume_filename || `${(app.student_name || "candidate").replace(/\s+/g, "_")}_resume.pdf`}
            hasResume={!!app.has_resume || !!app.resume_id}
          />
          {(() => {
            const active = interviews.find(iv => ["proposed", "scheduled"].includes(iv.status));
            if (active) {
              const label = active.status === "proposed" ? "Awaiting slot pick" : "Interview scheduled";
              return (
                <button
                  type="button"
                  onClick={() => setTab("interviews")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 transition"
                  data-testid="drawer-view-active-interview"
                  title={`Round ${active.round_no} · ${active.round_name || "Interview"}`}
                >
                  <Clock size={12} /> {label} · view
                </button>
              );
            }
            return (
              <button
                type="button"
                onClick={() => setShowScheduler(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border bg-white text-violet-700 hover:bg-violet-50 transition"
                style={{ borderColor: "rgb(153 51 255 / 0.3)" }}
                data-testid="drawer-schedule-interview"
              >
                <Calendar size={12} /> {interviews.length > 0 ? `Next round (${interviews.length + 1})` : "Schedule interview"}
              </button>
            );
          })()}
        </div>
        <SendOfferRow app={app} />

        <div className="border-b flex" style={{ borderColor: "var(--rule)" }}>
          {[
            { key: "interviews", label: `Interviews (${interviews.length})`, icon: Calendar },
            { key: "resume", label: "Resume", icon: FileText },
            { key: "ai", label: "AI Insights", icon: Sparkles },
            { key: "notes", label: `Notes (${notes.length})`, icon: MessageSquare },
            { key: "score", label: `Scorecard${scorecard.average ? ` · ${scorecard.average}` : ""}`, icon: Star },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`drawer-tab-${t.key}`}
              className="flex-1 px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition border-b-2"
              style={{
                color: tab === t.key ? "var(--signal)" : "var(--muted)",
                borderColor: tab === t.key ? "var(--signal)" : "transparent",
              }}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{err}</div>}

          {tab === "interviews" && (
            <>
              {interviews.length === 0 ? (
                <div className="text-sm text-muted text-center py-6" data-testid="interviews-empty">
                  No interviews yet. Click <em>Schedule interview</em> above to propose slots.
                </div>
              ) : interviews.map((iv) => (
                <InterviewCard
                  key={iv.id}
                  iv={iv}
                  onFeedback={() => setFeedbackFor(iv)}
                  onChanged={() => { refresh(); onChanged?.(); }}
                />
              ))}
            </>
          )}

          {tab === "ai" && <ResumeInsights appId={app.id} hasResume={!!app.has_resume || !!app.resume_id} />}

          {tab === "resume" && (
            <ResumePreview
              appId={app.id}
              filename={app.resume_filename || `${(app.student_name || "candidate").replace(/\s+/g, "_")}_resume.pdf`}
              hasResume={!!app.has_resume || !!app.resume_id}
            />
          )}

          {tab === "notes" && (
            <>
              {notes.length === 0 ? (
                <div className="text-sm text-muted text-center py-6">No notes yet — drop the first one below.</div>
              ) : notes.map((n) => (
                <div key={n.id} className="rounded-xl border p-3" style={{ borderColor: "var(--rule)" }} data-testid={`note-${n.id}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold">{n.author_name || n.author_id}</span>
                    <span className="text-muted">{timeAgo(n.created_at)}</span>
                  </div>
                  <div className="mt-1.5 text-sm whitespace-pre-wrap">{n.text}</div>
                </div>
              ))}
            </>
          )}

          {tab === "score" && (
            <>
              {scorecard.average !== null && (
                <div className="rounded-xl bg-paper border p-3 text-center" style={{ borderColor: "var(--rule)" }}>
                  <div className="text-xs uppercase tracking-wider text-muted">Team Average</div>
                  <div className="text-3xl font-display font-black mt-1" style={{ color: "#9933FF" }}>{scorecard.average}</div>
                  <div className="text-xs text-muted">{scorecard.count} rating{scorecard.count === 1 ? "" : "s"}</div>
                </div>
              )}
              {scorecard.items.length === 0 ? (
                <div className="text-sm text-muted text-center py-4">No ratings yet — leave the first below.</div>
              ) : scorecard.items.map((s) => (
                <div key={s.id} className="rounded-xl border p-3" style={{ borderColor: "var(--rule)" }}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold">{s.rater_name}</span>
                    <span className="inline-flex items-center gap-0.5 font-bold" style={{ color: "#9933FF" }}>
                      {[1,2,3,4,5].map((i) => (
                        <Star key={i} size={14} className={i <= s.rating ? "fill-current" : ""} />
                      ))}
                    </span>
                  </div>
                  {s.comment && <div className="mt-1.5 text-sm">{s.comment}</div>}
                </div>
              ))}
            </>
          )}
        </div>

        {/* footer composer */}
        <footer className="p-4 border-t" style={{ borderColor: "var(--rule)" }}>
          {tab === "notes" ? (
            <form onSubmit={submitNote} className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="Add a team-visible note…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                data-testid="note-input"
              />
              <button type="submit" disabled={busy || !noteText.trim()} className="btn-primary disabled:opacity-50" data-testid="note-submit">
                <Send size={14} /> Post
              </button>
            </form>
          ) : (
            <form onSubmit={submitScore} className="space-y-2">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((i) => (
                  <button key={i} type="button" onClick={() => setRating(i)} data-testid={`star-${i}`}
                    className="p-1 transition-transform hover:scale-110">
                    <Star size={22} className={i <= rating ? "fill-current" : ""} style={{ color: "#9933FF" }} />
                  </button>
                ))}
                <span className="text-sm text-muted ml-1">{rating ? `${rating}/5` : "Tap to rate"}</span>
              </div>
              <div className="flex gap-2">
                <input
                  className="input flex-1"
                  placeholder="Why this rating? (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  data-testid="score-comment"
                />
                <button type="submit" disabled={busy || !rating} className="btn-primary disabled:opacity-50" data-testid="score-submit">
                  Save
                </button>
              </div>
            </form>
          )}
        </footer>
      </aside>
      {showScheduler && (
        <InterviewSchedulerModal
          appId={app.id}
          candidateName={app.student_name || "the candidate"}
          defaultRoundName={interviews.length === 0 ? "Tech round 1" : `Round ${interviews.length + 1}`}
          onClose={() => setShowScheduler(false)}
          onScheduled={() => { refresh(); onChanged?.(); }}
        />
      )}
      {feedbackFor && (
        <InterviewFeedbackModal
          interview={feedbackFor}
          onClose={() => setFeedbackFor(null)}
          onSubmitted={() => { refresh(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

/* ------------------------- interview card (drawer) ------------------------- */
const IV_STATUS_COLORS = {
  proposed:  "#0099FF",
  scheduled: "#16A34A",
  completed: "#0F7C39",
  no_show:   "#DC2626",
  cancelled: "#94A3B8",
};
const IV_DECISION_LABEL = {
  strong_yes: "Strong Yes", yes: "Yes", maybe: "Maybe", no: "No", strong_no: "Strong No",
};

function InterviewCard({ iv, onFeedback, onChanged }) {
  const [busy, setBusy] = useState(false);
  const color = IV_STATUS_COLORS[iv.status] || "#94A3B8";

  const noShow = async () => {
    if (!window.confirm(`Mark ${iv.round_name} as no-show?`)) return;
    setBusy(true);
    try {
      await api.post(`/interviews/${iv.id}/no-show`, { note: "Marked from pipeline drawer" });
      onChanged?.();
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: "var(--rule)" }} data-testid={`interview-card-${iv.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ background: color }}>
              {iv.status.replace("_", " ")}
            </span>
            <span className="font-display font-bold text-sm">Round {iv.round_no}</span>
          </div>
          <div className="text-xs text-muted mt-0.5 truncate">{iv.round_name}</div>
        </div>
        {iv.outcome_decision && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-paper border" style={{ borderColor: "var(--rule)" }}>
            {IV_DECISION_LABEL[iv.outcome_decision]}
            {iv.outcome_rating ? ` · ${iv.outcome_rating}/5` : ""}
          </span>
        )}
      </div>

      <div className="text-[11px] text-muted space-y-0.5">
        {iv.mode === "video" && iv.meeting_link && (
          <div className="inline-flex items-center gap-1.5 flex-wrap">
            <Video size={11} />
            <a href={iv.meeting_link} target="_blank" rel="noopener noreferrer" className="text-signal hover:underline truncate max-w-[260px]" data-testid={`iv-link-${iv.id}`}>
              {iv.meeting_link}
            </a>
            {/teams\.microsoft\.com|teams\.live\.com/i.test(iv.meeting_link) && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded text-white" style={{ background: "#4641DC" }} data-testid={`iv-teams-${iv.id}`}>
                Teams
              </span>
            )}
            {/meet\.google\.com/i.test(iv.meeting_link) && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded text-white" style={{ background: "#0F9D58" }}>Meet</span>
            )}
            {/zoom\.us/i.test(iv.meeting_link) && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded text-white" style={{ background: "#2D8CFF" }}>Zoom</span>
            )}
          </div>
        )}
        {iv.mode === "onsite" && iv.venue && (
          <div className="inline-flex items-center gap-1"><MapPin size={11} /> {iv.venue}</div>
        )}
        {iv.interviewer_name && (
          <div className="inline-flex items-center gap-1"><Mail size={11} /> {iv.interviewer_name}{iv.interviewer_email ? ` · ${iv.interviewer_email}` : ""}</div>
        )}
        {iv.accepted_slot ? (
          <div className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={11} /> {new Date(iv.accepted_slot).toLocaleString()}</div>
        ) : (
          <div>Slots: {(iv.slots || []).map((s) => new Date(s).toLocaleString()).join("  ·  ")}</div>
        )}
      </div>

      <a
        href={`${process.env.REACT_APP_BACKEND_URL}/api/interviews/${iv.id}/ics`}
        download
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-signal hover:underline"
        data-testid={`iv-ics-${iv.id}`}
      >
        <Calendar size={11} /> Download .ics invite
      </a>

      {iv.feedback?.length > 0 && (
        <div className="text-[11px] bg-paper rounded-md p-2 space-y-1">
          {iv.feedback.map((f) => (
            <div key={f.id}>
              <span className="font-semibold">{IV_DECISION_LABEL[f.decision]}</span> · {f.rating}/5
              {f.strengths && <div className="text-muted">+ {f.strengths}</div>}
              {f.concerns && <div className="text-muted">! {f.concerns}</div>}
            </div>
          ))}
        </div>
      )}

      {(iv.status === "proposed" || iv.status === "scheduled") && (
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={onFeedback}
            data-testid={`iv-complete-${iv.id}`}
            className="flex-1 text-[11px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-md bg-signal text-white hover:bg-signalDark transition"
          >
            Complete + feedback
          </button>
          <button
            type="button"
            onClick={noShow}
            disabled={busy}
            data-testid={`iv-noshow-${iv.id}`}
            className="text-[11px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
          >
            No-show
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------- main pipeline page ------------------------- */
export default function CompanyPipeline() {
  const [sp, setSp] = useSearchParams();
  const initialPid = sp.get("posting") || "";
  const [postings, setPostings] = useState([]);
  const [pid, setPid] = useState(initialPid);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [openApp, setOpenApp] = useState(null);
  const nav = useNavigate();

  // Load company's own postings. The public `/api/postings` endpoint
  // does NOT honour a `mine` query param — it returns every active
  // posting on the platform, which caused a subtle 403 bug on the
  // pipeline page: the dropdown was populated with postings the
  // recruiter did NOT own (any other company's posting on the
  // platform), and selecting one would then 403 on
  // `/postings/{pid}/pipeline` (ownership check). Fix — use the
  // dedicated `/api/postings/mine` endpoint that filters by
  // `posted_by=<current user>`.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/postings/mine");
        if (!alive) return;
        setPostings(r.data || []);
        if (!pid && r.data?.length) setPid(r.data[0].id);
      } catch (e) {
        if (alive) setErr(formatError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  const loadPipeline = useCallback(async () => {
    if (!pid) return;
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/postings/${pid}/pipeline`);
      setPipeline(r.data);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { if (pid) { loadPipeline(); setSp({ posting: pid }, { replace: true }); } }, [pid, loadPipeline, setSp]);

  const setStatus = async (app, status) => {
    try {
      await api.patch(`/applications/${app.id}/status`, { status });
      await loadPipeline();
    } catch (e) { setErr(formatError(e)); }
  };
  const onAdvance = (app) => setStatus(app, NEXT_STAGE[app.status]);
  const onReject  = (app) => setStatus(app, "rejected");

  const stageData = useMemo(() => {
    const map = new Map((pipeline?.stages || []).map((s) => [s.key, s.items]));
    return STAGES.map((s) => ({ ...s, items: map.get(s.key) || [] }));
  }, [pipeline]);

  return (
    <div className="space-y-5" data-testid="company-pipeline">
      <div className="rounded-sm p-6 text-white bg-brand-gradient flex items-start justify-between gap-3 flex-wrap" data-testid="pipeline-hero">
        <div>
          <span className="eyebrow on-grad-muted">Hiring Pipeline</span>
          <h2 className="font-display font-black text-3xl md:text-4xl mt-1 text-white">Track every applicant.</h2>
          <p className="on-grad-muted text-sm mt-2 max-w-xl">
            Move candidates through Screening → Interview → Offer → Hired. Leave team notes, rate fit, and ship roles faster.
          </p>
        </div>
        <button onClick={loadPipeline} className="btn-ghost-dark" data-testid="pipeline-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="card card-pad flex items-center gap-3 flex-wrap">
        <Users size={16} className="text-signal shrink-0" />
        <div className="font-semibold whitespace-nowrap">Posting:</div>
        {postings.length === 0 ? (
          <div className="text-sm text-muted">
            No postings yet — <button onClick={() => nav("/company/new")} className="text-signal font-semibold hover:underline">create one</button> to start hiring.
          </div>
        ) : (
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <select
              value={pid}
              onChange={(e) => setPid(e.target.value)}
              className="input pr-9 appearance-none"
              data-testid="posting-selector"
            >
              {postings.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted" />
          </div>
        )}
        {pipeline && (
          <div className="ml-auto text-xs text-muted">
            <CheckCircle2 size={12} className="inline mr-1 text-signal" />
            <b>{pipeline.total}</b> total applicants
          </div>
        )}
      </div>

      {err && (
        <div className="card card-pad text-red-600 inline-flex items-center gap-2" data-testid="pipeline-error">
          <XCircle size={16} /> {err}
        </div>
      )}

      {loading && (
        <div className="card card-pad animate-pulse text-muted">Loading pipeline…</div>
      )}

      {pipeline && !loading && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {stageData.map((s) => (
              <Column
                key={s.key}
                stage={s}
                items={s.items}
                onOpen={setOpenApp}
                onAdvance={onAdvance}
                onReject={onReject}
              />
            ))}
          </div>
        </div>
      )}

      {openApp && (
        <DetailDrawer
          app={openApp}
          onClose={() => setOpenApp(null)}
          onChanged={loadPipeline}
        />
      )}
    </div>
  );
}


/* ------------------------- Send Offer ------------------------- */
function SendOfferRow({ app }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({
    title: app.posting_title || "Offer",
    stipend: "",
    salary: "",
    duration: "",
    location: "",
    start_date: "",
    expires_at: "",
    body: `Hi ${app.student_name || "candidate"},\n\nWe’re excited to extend an offer for the position above.`,
  });
  const send = async () => {
    setBusy(true); setMsg("");
    try { await api.post(`/applications/${app.id}/offer`, form); setMsg("Offer sent ✓"); setTimeout(()=>{ setOpen(false); setBusy(false); setMsg(""); }, 1200); }
    catch (e) { setMsg(formatError(e)); setBusy(false); }
  };
  if (app.status === "hired") return null;
  return (
    <div className="px-5 py-3 border-b bg-green-50/50" style={{ borderColor: "var(--rule)" }}>
      {!open ? (
        <button type="button" className="w-full btn-primary justify-center" onClick={() => setOpen(true)} data-testid={`send-offer-${app.id}`}>
          <Send size={14}/> Send Offer Letter
        </button>
      ) : (
        <div className="space-y-2" data-testid={`offer-form-${app.id}`}>
          <input className="input" placeholder="Offer title" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="offer-title"/>
          <div className="grid grid-cols-2 gap-2">
            <input className="input" placeholder="Stipend" value={form.stipend} onChange={e=>setForm({...form,stipend:e.target.value})}/>
            <input className="input" placeholder="Salary" value={form.salary} onChange={e=>setForm({...form,salary:e.target.value})}/>
            <input className="input" placeholder="Duration" value={form.duration} onChange={e=>setForm({...form,duration:e.target.value})}/>
            <input className="input" placeholder="Location" value={form.location} onChange={e=>setForm({...form,location:e.target.value})}/>
            <input className="input" type="date" placeholder="Start date" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})}/>
            <input className="input" type="date" placeholder="Expires at" value={form.expires_at} onChange={e=>setForm({...form,expires_at:e.target.value})}/>
          </div>
          <textarea className="input min-h-[80px]" value={form.body} onChange={e=>setForm({...form,body:e.target.value})}/>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" disabled={busy} className="btn-primary disabled:opacity-50" onClick={send} data-testid="offer-submit">
              <Send size={14}/> {busy ? "Sending…" : "Send offer"}
            </button>
          </div>
          {msg && <p className="text-xs text-green-700">{msg}</p>}
        </div>
      )}
    </div>
  );
}
